/**
 * parts/core.js — shared construction primitives for the asset factory.
 *
 * Geometry and material memoisation, the mesh helper, canvas signage, and the
 * small geometry utilities the part modules build on. `factory.js` re-exports
 * the public members of this file; everything else in `parts/` imports it.
 *
 * @module assets3d/parts/core
 */

import * as THREE from 'three';
import { ART, CSS, css, shade, tint } from '../palette.js';
import { showcardText, bulbBand } from '../textures.js';

const geoCache = new Map();
const matCache = new Map();

/**
 * Memoised geometry.
 * @param {string} key
 * @param {() => THREE.BufferGeometry} build
 * @returns {THREE.BufferGeometry}
 */
export function geo(key, build) {
  let g = geoCache.get(key);
  if (!g) { g = build(); geoCache.set(key, g); }
  return g;
}

/**
 * Memoised standard material.
 *
 * Roughness sits high by default — this is a painted-canvas-and-timber
 * fairground, and a shiny midway looks like plastic.
 *
 * TWO THINGS TO KNOW BEFORE YOU MUTATE WHAT THIS RETURNS:
 *
 * 1. **Results are SHARED.** Two calls with the same arguments return the same
 *    object, which is the whole point — twelve booths must not allocate twelve
 *    materials. Writing to it changes every mesh already using it. Materials
 *    from here carry `_ccMemo = true` so `setEmissive()` can detect this and
 *    copy before writing.
 * 2. **`flat: true` returns a MeshBasicMaterial**, which has NO `.emissive`
 *    channel. `material.emissive.set(…)` on one throws and takes the frame
 *    loop with it. Use `setEmissive()` rather than touching `.emissive`.
 *
 * @param {number|string} color
 * @param {{ rough?: number, metal?: number, flat?: boolean, transparent?: boolean,
 *           opacity?: number, emissive?: number, map?: THREE.Texture,
 *           alphaMap?: THREE.Texture, side?: THREE.Side, emissiveIntensity?: number,
 *           depthWrite?: boolean }} [opts]
 * @returns {THREE.Material}
 */
export function mat(color, opts = {}) {
  const texKey = `${opts.map?.uuid || ''}|${opts.alphaMap?.uuid || ''}`;
  const key = `${color}|${JSON.stringify({ ...opts, map: undefined, alphaMap: undefined })}|${texKey}`;
  let m = matCache.get(key);
  if (m) return m;

  if (opts.flat) {
    m = new THREE.MeshBasicMaterial({
      color,
      map: opts.map || null,
      alphaMap: opts.alphaMap || null,
      transparent: !!opts.transparent || !!opts.alphaMap,
      opacity: opts.opacity ?? 1,
      side: opts.side ?? THREE.FrontSide,
      depthWrite: opts.depthWrite ?? true,
    });
  } else {
    m = new THREE.MeshStandardMaterial({
      color,
      map: opts.map || null,
      alphaMap: opts.alphaMap || null,
      roughness: opts.rough ?? 0.8,
      metalness: opts.metal ?? 0.04,
      transparent: !!opts.transparent || !!opts.alphaMap,
      opacity: opts.opacity ?? 1,
      emissive: opts.emissive ?? 0x000000,
      emissiveIntensity: opts.emissive ? (opts.emissiveIntensity ?? 1) : 0,
      side: opts.side ?? THREE.FrontSide,
      depthWrite: opts.depthWrite ?? true,
      alphaTest: opts.alphaMap ? 0.42 : 0,
    });
  }
  // Marks this instance as cache-owned, so setEmissive() knows to copy before
  // writing rather than repainting every mesh that shares it.
  m._ccMemo = true;
  matCache.set(key, m);
  return m;
}

/**
 * Apply an emissive glow safely, whatever you hand it.
 *
 * Solves two traps that both look like "the glow just doesn't work", and one
 * that kills the frame loop outright:
 *
 * - **Unlit materials have no emissive channel.** `mat(c, { flat: true })`
 *   returns a MeshBasicMaterial, and `material.emissive.set(…)` on one throws
 *   a TypeError inside the render loop. Here it is a silent no-op — an unlit
 *   material already draws at full brightness, so there is nothing to add.
 * - **Materials from `mat()` are shared.** Writing emissive straight onto one
 *   lights up every other mesh using it. Cache-owned materials are copied
 *   first, and the copy is assigned back when you pass a Mesh or a Group.
 * - Passing a whole model (a Group from any `create*` function) just works:
 *   every mesh under it is handled.
 *
 * ```js
 * setEmissive(fish, 0xffd23f, 0.5);          // a whole model, assigned back
 * setEmissive(mesh, 0x00ff88);               // one mesh, assigned back
 * m = setEmissive(m, 0x00ff88);              // a bare material — USE THE RETURN
 * setEmissive(fish, 0x000000, 0);            // and back off again
 * ```
 *
 * Safe to call every frame: the copy is made once, on the first write, and
 * subsequent calls write straight to the model's own material.
 *
 * @param {THREE.Object3D|THREE.Material|null} target Model, mesh, or material.
 * @param {number|string} color Emissive colour.
 * @param {number} [intensity]
 * @returns {THREE.Object3D|THREE.Material|null} The target, or — when given a
 *   bare material — the material to use, which may be a copy.
 */
export function setEmissive(target, color, intensity = 1) {
  if (!target) return target;

  if (/** @type {any} */ (target).isMaterial) {
    return applyEmissive(/** @type {THREE.Material} */ (target), color, intensity);
  }

  if (/** @type {any} */ (target).isObject3D) {
    /** @type {THREE.Object3D} */ (target).traverse((o) => {
      const m = /** @type {any} */ (o).material;
      if (!m) return;
      if (Array.isArray(m)) {
        /** @type {any} */ (o).material = m.map((x) => applyEmissive(x, color, intensity));
      } else {
        /** @type {any} */ (o).material = applyEmissive(m, color, intensity);
      }
    });
    return target;
  }
  return target;
}

/**
 * @param {THREE.Material} material
 * @param {number|string} color
 * @param {number} intensity
 * @returns {THREE.Material} The same material, or a copy safe to write to.
 */
function applyEmissive(material, color, intensity) {
  if (!material || !material.isMaterial) return material;
  // No emissive channel (MeshBasicMaterial, LineBasicMaterial, …). Nothing to
  // do — and writing would throw.
  if (!(/** @type {any} */ (material).emissive)) return material;

  let m = material;
  if (/** @type {any} */ (m)._ccMemo) {
    m = m.clone();
    // The copy belongs to whoever asked for it, not the cache.
    /** @type {any} */ (m)._ccMemo = false;
  }
  /** @type {any} */ (m).emissive.set(color);
  /** @type {any} */ (m).emissiveIntensity = intensity;
  m.needsUpdate = true;
  return m;
}

/**
 * Build a mesh with sensible shadow defaults.
 * @param {THREE.BufferGeometry} geometry
 * @param {THREE.Material} material
 * @param {{ cast?: boolean, receive?: boolean }} [opts]
 * @returns {THREE.Mesh}
 */
export function mesh(geometry, material, { cast = true, receive = true } = {}) {
  const m = new THREE.Mesh(geometry, material);
  m.castShadow = cast;
  m.receiveShadow = receive;
  return m;
}

/**
 * An empty child transform, used for cosmetic and gameplay anchors.
 * @param {THREE.Object3D} parent
 * @param {string} name
 * @param {number} x @param {number} y @param {number} z
 * @returns {THREE.Object3D}
 */
export function makeAnchor(parent, name, x, y, z) {
  const a = new THREE.Object3D();
  a.name = name;
  a.position.set(x, y, z);
  parent.add(a);
  return a;
}

/**
 * A small emissive bulb. Unlit material so it glows without costing a light.
 * @param {number} [radius]
 * @param {number} [color]
 * @returns {THREE.Mesh}
 */
export function bulb(radius = 0.055, color = ART.bulbWarm) {
  const m = mesh(
    geo(`bulb-${radius}`, () => new THREE.SphereGeometry(radius, 8, 6)),
    mat(color, { flat: true, transparent: true, opacity: 0.96 }),
    { cast: false, receive: false },
  );
  m.name = 'bulb';
  return m;
}

/**
 * A ball finial — the turned wooden knob on top of a post.
 * @param {number} [radius]
 * @param {number} [color]
 * @returns {THREE.Mesh}
 */
export function finial(radius = 0.09, color = ART.gold) {
  return mesh(
    geo(`finial-${radius}`, () => new THREE.SphereGeometry(radius, 10, 8)),
    mat(color, { rough: 0.55, metal: 0.25 }),
  );
}

/**
 * A rounded slab: a box with bevelled edges, which catches light far better
 * than a hard-edged primitive at this art scale.
 * @param {number} w @param {number} h @param {number} d
 * @param {number} [bevel]
 * @returns {THREE.BufferGeometry}
 */
export function slab(w, h, d, bevel = 0.02) {
  return geo(`slab-${w}-${h}-${d}-${bevel}`, () => {
    const g = new THREE.BoxGeometry(w, h, d, 1, 1, 1);
    const pos = g.attributes.position;
    const v = new THREE.Vector3();
    for (let i = 0; i < pos.count; i++) {
      v.fromBufferAttribute(pos, i);
      // Pull every corner vertex toward the centre by the bevel amount, which
      // reads as a chamfer once normals are recomputed.
      v.x -= Math.sign(v.x) * bevel;
      v.y -= Math.sign(v.y) * bevel;
      v.z -= Math.sign(v.z) * bevel;
      pos.setXYZ(i, v.x, v.y, v.z);
    }
    g.computeVertexNormals();
    return g;
  });
}

/**
 * Sweep a tube along a smooth curve — necks, ropes, rod tips, catenaries.
 * @param {THREE.Vector3[]} points
 * @param {number} radius
 * @param {{ segments?: number, radial?: number, closed?: boolean,
 *           taper?: number, key?: string }} [opts] `taper` shrinks the far end.
 * @returns {THREE.BufferGeometry}
 */
export function tube(points, radius, opts = {}) {
  const segments = opts.segments ?? 16;
  const radial = opts.radial ?? 8;
  const key = opts.key
    && `tube-${opts.key}-${radius}-${segments}-${radial}-${opts.taper ?? 1}`;
  const build = () => {
    const curve = new THREE.CatmullRomCurve3(points, !!opts.closed);
    const g = new THREE.TubeGeometry(curve, segments, radius, radial, !!opts.closed);
    const taper = opts.taper ?? 1;
    if (taper !== 1) {
      // TubeGeometry lays vertices out ring by ring, so the ring index maps
      // straight onto position along the curve.
      const pos = g.attributes.position;
      const perRing = radial + 1;
      const rings = segments + 1;
      const centres = [];
      for (let r = 0; r < rings; r++) {
        centres.push(curve.getPointAt(Math.min(1, r / segments)));
      }
      const v = new THREE.Vector3();
      for (let i = 0; i < pos.count; i++) {
        const ring = Math.min(rings - 1, Math.floor(i / perRing));
        const k = 1 + (taper - 1) * (ring / (rings - 1));
        v.fromBufferAttribute(pos, i).sub(centres[ring]).multiplyScalar(k).add(centres[ring]);
        pos.setXYZ(i, v.x, v.y, v.z);
      }
      g.computeVertexNormals();
    }
    return g;
  };
  return key ? geo(key, build) : build();
}

/**
 * A plump teardrop body: a sphere squeezed to a broad chest and drawn out to a
 * raised, tapering tail. This is the goose's whole silhouette, so it is worth
 * a real shape rather than a scaled sphere.
 *
 * @param {number} r Nominal radius.
 * @param {{ seg?: number, taper?: number, lift?: number, chest?: number }} [o]
 * @returns {THREE.BufferGeometry}
 */
export function teardrop(r, o = {}) {
  const seg = o.seg ?? 18;
  const taper = o.taper ?? 0.42;
  const lift = o.lift ?? 0.30;
  const chest = o.chest ?? 1.10;
  return geo(`teardrop-${r}-${seg}-${taper}-${lift}-${chest}`, () => {
    const g = new THREE.SphereGeometry(r, seg, Math.round(seg * 0.7));
    const pos = g.attributes.position;
    const v = new THREE.Vector3();
    for (let i = 0; i < pos.count; i++) {
      v.fromBufferAttribute(pos, i);
      // t: 0 at the front (−Z, the chest), 1 at the back (+Z, the tail).
      const t = THREE.MathUtils.clamp((v.z / r + 1) / 2, 0, 1);
      const width = THREE.MathUtils.lerp(chest, taper, Math.pow(t, 1.45));
      v.x *= width;
      v.y *= width;
      v.y += lift * r * Math.pow(t, 2.1);
      pos.setXYZ(i, v.x, v.y, v.z);
    }
    g.computeVertexNormals();
    return g;
  });
}

/**
 * A webbed foot outline, extruded flat. Three toes joined by webbing.
 * @param {number} [len]
 * @returns {THREE.BufferGeometry}
 */
export function webbedFoot(len = 0.17) {
  return geo(`web-foot-${len}`, () => {
    const s = new THREE.Shape();
    const w = len * 0.62;
    s.moveTo(0, len * 0.32);                       // heel
    s.quadraticCurveTo(-w * 0.55, len * 0.10, -w * 0.62, -len * 0.44);
    s.quadraticCurveTo(-w * 0.30, -len * 0.30, -w * 0.14, -len * 0.52);  // toe 1 web
    s.quadraticCurveTo(0, -len * 0.32, w * 0.14, -len * 0.52);           // toe 2 web
    s.quadraticCurveTo(w * 0.30, -len * 0.30, w * 0.62, -len * 0.44);
    s.quadraticCurveTo(w * 0.55, len * 0.10, 0, len * 0.32);
    const g = new THREE.ExtrudeGeometry(s, {
      depth: len * 0.10, bevelEnabled: true,
      bevelThickness: len * 0.03, bevelSize: len * 0.03, bevelSegments: 1,
      curveSegments: 3,
    });
    g.rotateX(-Math.PI / 2);
    g.center();
    return g;
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Signage
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Legacy sign grounds from the greybox era, mapped onto the carnival palette.
 * Stall modules copied `bg: '#12172a'` from the exemplar, so remapping here
 * keeps every peer's signage on-palette without touching their files.
 */
const BG_REMAP = {
  '#12172a': CSS.plumDeep,
  '#1b2340': CSS.plum,
  '#1B2340': CSS.plum,
};

/**
 * Render text to a canvas texture. Shared by signage and UI panels.
 *
 * @param {string|string[]} text
 * @param {{ width?: number, height?: number, bg?: string, color?: string,
 *           font?: string, size?: number, align?: CanvasTextAlign,
 *           lines?: string[], radius?: number, border?: string,
 *           plain?: boolean, bulbs?: boolean }} [opts]
 * @returns {{ texture: THREE.CanvasTexture, canvas: HTMLCanvasElement,
 *             ctx: CanvasRenderingContext2D, update: (t: string|string[]) => void }}
 */
export function makeTextTexture(text, opts = {}) {
  const width = opts.width ?? 512;
  const height = opts.height ?? 256;
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');

  const bg = BG_REMAP[opts.bg] ?? opts.bg ?? CSS.plumDeep;
  const ink = opts.color ?? CSS.gold;
  const radius = opts.radius ?? Math.min(width, height) * 0.11;

  const draw = (content) => {
    const lines = (Array.isArray(content) ? content : [content]).map(String);
    ctx.clearRect(0, 0, width, height);

    if (opts.bg !== 'transparent') {
      // Plum ground with a soft vertical lift, so the plaque has some depth.
      const grad = ctx.createLinearGradient(0, 0, 0, height);
      grad.addColorStop(0, tintCss(bg, 0.16));
      grad.addColorStop(1, shadeCss(bg, 0.22));
      ctx.fillStyle = grad;
      roundRect(ctx, 0, 0, width, height, radius);
      ctx.fill();

      if (opts.border !== null) {
        ctx.strokeStyle = opts.border ?? CSS.gold;
        ctx.lineWidth = Math.max(5, height * 0.035);
        roundRect(ctx, ctx.lineWidth * 0.6, ctx.lineWidth * 0.6,
          width - ctx.lineWidth * 1.2, height - ctx.lineWidth * 1.2, radius * 0.85);
        ctx.stroke();

        ctx.strokeStyle = 'rgba(255,255,255,0.20)';
        ctx.lineWidth = Math.max(2, height * 0.012);
        roundRect(ctx, height * 0.055, height * 0.055,
          width - height * 0.11, height - height * 0.11, radius * 0.6);
        ctx.stroke();
      }
    }

    const size = opts.size ?? Math.floor(height / (lines.length + 0.85));
    const lineH = size * 1.24;
    const startY = height / 2 - ((lines.length - 1) * lineH) / 2;
    const x = (opts.align ?? 'center') === 'left' ? width * 0.06 : width / 2;

    if (opts.plain) {
      // Data readouts (scoreboards) want crisp, unornamented text.
      ctx.font = opts.font ?? `bold ${size}px "Trebuchet MS", sans-serif`;
      ctx.fillStyle = ink;
      ctx.textAlign = opts.align ?? 'center';
      ctx.textBaseline = 'middle';
      lines.forEach((line, i) => ctx.fillText(line, x, startY + i * lineH));
      return;
    }

    lines.forEach((line, i) => {
      // The first line is the headline; any others are supporting detail.
      const headline = i === 0 && lines.length > 1;
      showcardText(ctx, line, x, startY + i * lineH, {
        size: headline ? size : size * 0.82,
        fill: headline ? ink : CSS.cream,
        outline: shadeCss(bg, 0.55),
        highlight: 'rgba(255,255,255,0.30)',
        maxWidth: width * 0.88,
      });
    });
  };

  draw(text);

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.anisotropy = 4;

  return {
    texture,
    canvas,
    ctx,
    /** Redraw with new content. Cheap enough for a live scoreboard. */
    update(content) {
      draw(content);
      texture.needsUpdate = true;
    },
  };
}

/** @param {CanvasRenderingContext2D} ctx */
export function roundRect(ctx, x, y, w, h, r) {
  const rad = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rad, y);
  ctx.arcTo(x + w, y, x + w, y + h, rad);
  ctx.arcTo(x + w, y + h, x, y + h, rad);
  ctx.arcTo(x, y + h, x, y, rad);
  ctx.arcTo(x, y, x + w, y, rad);
  ctx.closePath();
}

function parseCss(c) {
  const s = String(c).replace('#', '');
  return parseInt(s.length === 3 ? s.split('').map((x) => x + x).join('') : s, 16);
}
const tintCss = (c, t) => css(tint(parseCss(c), t));
const shadeCss = (c, t) => css(shade(parseCss(c), t));

/**
 * A painted sign panel. The plaque itself is geometry, so signage has real
 * thickness and edge lighting rather than floating as a decal.
 *
 * @param {string} text
 * @param {{ w?: number, h?: number, bg?: string, color?: string, lines?: string[],
 *           doubleSided?: boolean, size?: number, border?: string|null,
 *           plain?: boolean, bulbs?: boolean, frame?: boolean }} [opts]
 * @returns {THREE.Mesh} with `.userData.setText(text)` for live updates.
 */
export function createSign(text, opts = {}) {
  const w = opts.w ?? 2.4;
  const h = opts.h ?? 0.7;
  // 400 px/m keeps small signs legible, with a floor so a 0.2 m label still
  // gets enough pixels, and a ceiling so a banner does not allocate 4 K.
  const px = Math.min(2048, Math.max(256, Math.round(w * 400)));
  const py = Math.min(2048, Math.max(72, Math.round(h * 400)));

  const tex = makeTextTexture(opts.lines || text, {
    width: px, height: py,
    bg: opts.bg, color: opts.color, size: opts.size,
    border: opts.border, plain: opts.plain, align: opts.align,
  });

  const m = new THREE.Mesh(
    new THREE.PlaneGeometry(w, h),
    new THREE.MeshBasicMaterial({
      map: tex.texture,
      transparent: true,
      side: opts.doubleSided ? THREE.DoubleSide : THREE.FrontSide,
      depthWrite: opts.bg === 'transparent' ? false : true,
    }),
  );
  m.name = 'sign';
  m.userData.setText = (t) => tex.update(t);
  m.userData.texture = tex;

  // Signs with a solid ground get a plum backing board, lifted a hair behind
  // the face. Transparent signs (marquee lettering) stay as pure decals.
  if (opts.bg !== 'transparent' && opts.frame !== false) {
    const back = mesh(
      slab(w * 1.045, h * 1.10, 0.045, 0.012),
      mat(ART.plumDeep, { rough: 0.82 }),
    );
    back.position.z = -0.028;
    back.name = 'sign-back';
    m.add(back);

    if (opts.bulbs) {
      const rail = mesh(
        new THREE.PlaneGeometry(w * 1.045, h * 0.09),
        mat(0xffffff, { flat: true, map: bulbBand(Math.max(6, Math.round(w * 5))) }),
        { cast: false, receive: false },
      );
      rail.position.set(0, h * 0.575, 0.004);
      m.add(rail);
      const rail2 = rail.clone();
      rail2.position.y = -h * 0.575;
      m.add(rail2);
    }
  }
  return m;
}

/** Dispose every cached geometry/material. Only for a full teardown. */
export function disposeCaches() {
  for (const g of geoCache.values()) g.dispose();
  for (const m of matCache.values()) m.dispose();
  geoCache.clear();
  matCache.clear();
}
